import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../../../../reducer/CombineReducer";
import { Actions } from "../OutputMappingButtonsBar/Types";
import { OutputConfigEditRootMap, OutputConfigRootMap } from "../../Api/Types";
import { OutputConfigSlice } from "../../redux";
import { SelectedReport } from "./Types";
import { ConfigDetailsFormView } from "./ConfigDetailsForm.view";

interface Props {
  /**
   * Popup action
   */
  action: string;
}

/**
 * Responsible for rendering Output Mapping popup
 * @returns Output Mapping popup view
 */
export const ConfigDetailsForm: React.FC<Props> = (props: Props) => {
  const { action } = props;
  const dispatch = useDispatch();
  const isConfigDetailsForm = (action === Actions.CREATE || action === Actions.MODIFY);

  const outputConfig = useSelector((state: IRootState) => state.outputConfig);
  const editRootMap = outputConfig.outputMappingPopupEditConfigData;
  const isChildItem = isConfigDetailsForm && outputConfig.selectedOutputMapping.band === Actions.CHILD;
  const [createData, setCreateData] = useState<OutputConfigRootMap>({} as OutputConfigRootMap);
  const [editData, setEditData] = useState<OutputConfigEditRootMap>({} as OutputConfigEditRootMap);
  const isCreate = (action === Actions.CREATE);
  const tagName = isCreate ? "" : editRootMap?.tag;

  const [selectedReportData, setSelectedReportData] = React.useState({} as SelectedReport);
  const [showPicker, setShowPicker] = React.useState(false);

  /**
   * Action on pick report
   */
  const onPickReport = (): void => {
    setShowPicker(true);
  };

  /**
   * Action on pick report
   */
  const onPickReportClose = (): void => {
    setShowPicker(false);
  };

  /**
   * Handler callback to select the report
   * @param report selected report item
   */
  const onSelectedReport = (report: SelectedReport): void => {
    setSelectedReportData(report);
    onPickReportClose();
  };

  /**
   * Initial form data for create action
   * @returns default value for create form
   */
  const createInitialData = (): OutputConfigRootMap => {
    return {
      configName: "",
      tag: "",
      sourceReport: {} as SelectedReport,
      includeInOutput: true
    };
  };

  /**
   * Initial form data for edit action
   * @returns default value for edit form
   */
  const editInitialData = (): OutputConfigEditRootMap => {
    return {
      configMapId: editRootMap.configMapId,
      configName: editRootMap.configName,
      tag: editRootMap.tag,
      sourceReport: isChildItem ? undefined : editRootMap.sourceReport,
      includeInOutput: editRootMap.includeInOutput
    };
  };

  /**
   * Check if it is a valid save
   * @param data Can be edit / create data
   * @returns if the save is valid
   */
  const isSaveValid = (data: OutputConfigRootMap | OutputConfigEditRootMap): boolean => {
    return (data.configName !== "" &&
      ((!isChildItem && data.sourceReport?.reportName !== undefined) || isChildItem) &&
      data.tag !== "");
  };


  /**
   * Action when config name value is changed
   * @param e React SyntheticEvent object
   */
  const onChangeValue = (e: React.SyntheticEvent<any, Event>): void => {
    const target = e.currentTarget;
    const name = target.name;
    const value = target.type === "checkbox" ? target.checked : target.value;

    if (isCreate) {
      const newData = { ...createData, [name]: value };
      setCreateData(newData);
    }
    else {
      const newData = { ...editData, [name]: value };
      setEditData(newData);
    }
  };

  const onIncludeXMLChange = (name: string) => (isSelected: boolean): void => {
    if (isCreate) {
      const newData = { ...createData, [name]: isSelected };
      setCreateData(newData);
    }
    else {
      const newData = { ...editData, [name]: isSelected };
      setEditData(newData);
    }
  };

  /**
   * Set default value during mount
   */
  useEffect(() => {
    if (isCreate) {
      setCreateData(createInitialData());
    }
    else {
      setEditData(editInitialData());
    }
  }, []);

  useEffect(() => {
    if (selectedReportData.reportId || selectedReportData.reportERN) {
      if (isCreate) {
        setCreateData({ ...createData, sourceReport: selectedReportData });
      } else {
        setEditData({ ...editData, sourceReport: selectedReportData });
      }
    }
  }, [selectedReportData]);

  /**
   * Actions to be handled when create form values are changed
   */
  useEffect(() => {
    if (isSaveValid(createData)) {
      dispatch(OutputConfigSlice.actions.setOutputMappingPopupConfigData(createData));
      dispatch(OutputConfigSlice.actions.setEnableMappingPopupSave(true));
    }
    else {
      dispatch(OutputConfigSlice.actions.setEnableMappingPopupSave(false));
    }
  }, [createData]);

  /**
   * Actions to be handled when edit form values are changed
   */
  useEffect(() => {
    if (isSaveValid(editData)) {
      dispatch(OutputConfigSlice.actions.setOutputMappingPopupEditConfigData(editData));
      dispatch(OutputConfigSlice.actions.setEnableMappingPopupSave(true));
    } else {
      dispatch(OutputConfigSlice.actions.setEnableMappingPopupSave(false));
    }
  }, [editData]);

  return (
    <ConfigDetailsFormView
      popupAction={action}
      selectedReport={onSelectedReport}
      isChildItem={isChildItem}
      onChange={onChangeValue}
      createData={createData}
      editData={editData}
      showPicker={showPicker}
      onPickReport={onPickReport}
      onPickReportClose={onPickReportClose}
      selectedReportData={selectedReportData}
      headerText={tagName}
      onIncludeXMLChange={onIncludeXMLChange}
    />
  );
};
